import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import AuthService from '../services/AuthService';
import { hasPermission, getDefaultAdminRoute } from '../utils/permissions';
import { useAdminCounts } from '../hooks/useAdminCounts';
import SubSidebar from './SubSidebar';
import './Sidebar.css';

// Small pill shown next to a menu title (expanded sidebar) or a dot on its
// icon (collapsed/icon-only rail) — reused for both the Bookings and
// Memberships nav entries.
const MenuBadge = ({ count }) => {
    if (!count) return null;
    return <span className="menu-badge">{count > 99 ? '99+' : count}</span>;
};

const MenuIconDot = ({ count }) => (count ? <span className="menu-icon-dot" /> : null);

const normalizePath = (path) => path.replace(/\/$/, '');

const isRouteMatch = (currentPath, routePath) => {
    const normalizedCurrent = normalizePath(currentPath);
    const normalizedRoute = normalizePath(routePath);
    return normalizedCurrent === normalizedRoute ||
        (normalizedRoute !== '' && normalizedCurrent.startsWith(normalizedRoute + '/'));
};

// Matches Bootstrap's `lg` breakpoint (the burger shows below 992px via d-lg-none)
// and the sidebar's `max-width: 991.98px` media query, so there is no width band
// where the burger is visible but the drawer styling hasn't kicked in.
const MOBILE_BREAKPOINT = 991.98;

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    // Off-canvas drawer state (mobile only). Desktop ignores this entirely.
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
    );
    const { membershipCount, subscriberCount, cancellationCount, bookingCount } = useAdminCounts();
    // Maps an item's `countKey` (see subMenus.memberships above) to its live badge count.
    const subMenuCounts = { subscribers: subscriberCount, cancellations: cancellationCount };

    const handleLogoutConfirm = () => {
        AuthService.logout();
        setShowLogoutModal(false);
    };

    const subMenus = useMemo(() => {
        const all = {
        catalogue: {
            items: [
                { path: '/admin/rooms',    title: 'Rooms',    icon: 'bi bi-door-open-fill', permission: 'rooms.index'    },
                { path: '/admin/services', title: 'Services', icon: 'bi bi-snow2',          permission: 'services.index' },
            ],
            icon: 'bi bi-database-fill'
        },
        memberships: {
            items: [
                { path: '/admin/membership-plans',                         title: 'Plans',                 icon: 'bi bi-card-checklist',  permission: 'membership-plans.index'                       },
                { path: '/admin/membership-plans/subscribers',             title: 'Subscribers',           icon: 'bi bi-person-badge',    permission: 'membership-plans.subscribers',           countKey: 'subscribers'   },
                { path: '/admin/membership-plans/cancellation-requests',   title: 'Cancellation Requests', icon: 'bi bi-x-octagon',       permission: 'membership-plans.cancellation-requests', countKey: 'cancellations' },
            ],
            icon: 'bi bi-award-fill'
        },
        teamManagement: {
            items: [
                { path: '/admin/system-users', title: 'System Users', icon: 'bi bi-people-fill', permission: 'system-users.index' },
                { path: '/admin/roles', title: 'Roles & Permissions', icon: 'bi bi-shield-check', permission: 'roles.index' },
                { path: '/admin/attendance', title: 'Attendance Logs', icon: 'bi bi-clock-history', permission: 'attendance.index' },
            ],
            icon: 'bi bi-people-fill'
        },
        };

        // Drop items this role cannot open, then drop any group left empty —
        // a parent menu that opens onto nothing is worse than no parent at all.
        return Object.fromEntries(
            Object.entries(all)
                .map(([key, menu]) => [
                    key,
                    { ...menu, items: menu.items.filter((item) => hasPermission(item.permission)) },
                ])
                .filter(([, menu]) => menu.items.length > 0)
        );
    }, []);

    const getActiveSubMenu = useCallback((pathname) => {
        const normalizedPath = normalizePath(pathname);

        if (normalizedPath === '/admin/settings') return null;

        const teamRoutes = ['/admin/system-users', '/admin/roles', '/admin/attendance'];
        if (subMenus.teamManagement && teamRoutes.some(route => isRouteMatch(normalizedPath, route))) {
            return 'teamManagement';
        }

        for (const [menuKey, menuData] of Object.entries(subMenus)) {
            const hasMatch = menuData.items.some(item => isRouteMatch(normalizedPath, item.path));
            if (hasMatch) return menuKey;
        }

        return null;
    }, [subMenus]);

    useEffect(() => {
        const activeMenu = getActiveSubMenu(location.pathname);
        setActiveSubMenu(activeMenu);
        document.body.classList.toggle('sub-sidebar-active', activeMenu !== null);
    }, [location.pathname, getActiveSubMenu]);

    const handleMenuClick = useCallback((menuKey) => {
        // On mobile the drawer has no room for the separate sub-sidebar panel, so
        // the parent acts as an accordion that expands its items inline instead.
        if (isMobile) {
            setActiveSubMenu(prev => (prev === menuKey ? null : menuKey));
            return;
        }
        const menu = subMenus[menuKey];
        const firstItem = menu?.items[0];
        if (firstItem) navigate(firstItem.path);
    }, [isMobile, navigate, subMenus]);

    // Track viewport so the drawer/accordion behaviour switches cleanly on resize.
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Wire the header burger (rendered in Header.jsx) to the drawer.
    useEffect(() => {
        const button = document.getElementById('kt_app_sidebar_mobile_toggle');
        const toggle = () => setMobileOpen(open => !open);
        if (button) button.addEventListener('click', toggle);
        return () => {
            if (button) button.removeEventListener('click', toggle);
        };
    }, []);

    // Any navigation closes the drawer so the freshly opened page is visible.
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // The collapsed/sub-sidebar rail is a desktop-only affordance; on mobile the
    // drawer always shows full-width labels.
    const isSidebarCollapsed = !isMobile && activeSubMenu !== null;

    const renderMobileSubItems = (menuKey) => {
        if (!isMobile || activeSubMenu !== menuKey || !subMenus[menuKey]) return null;
        return (
            <div className="menu-mobile-sub">
                {subMenus[menuKey].items.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className="menu-link menu-sublink"
                    >
                        <span className="menu-icon"><i className={item.icon}></i></span>
                        <span className="menu-title">{item.title}</span>
                        {item.countKey && <MenuBadge count={subMenuCounts[item.countKey]} />}
                    </NavLink>
                ))}
            </div>
        );
    };

    return (
        <>
        {mobileOpen && (
            <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
        )}
        <div id="kt_app_sidebar" className={`app-sidebar admin-sidebar flex-column ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`} data-kt-drawer="true" data-kt-drawer-name="app-sidebar" data-kt-drawer-activate="{default: true, lg: false}" data-kt-drawer-overlay="true" data-kt-drawer-width="280px" data-kt-drawer-direction="start" data-kt-drawer-toggle="#kt_app_sidebar_mobile_toggle">
            <div className="sidebar-header p-2 pb-1 d-flex justify-content-start align-items-center position-relative border-bottom">
                <img src={logo} alt="LM" className={`logo-image mx-5 ${isSidebarCollapsed ? 'w-50px' : 'w-70px'}`} />
            </div>

            {isSidebarCollapsed && (
                <button
                    className="sidebar-toggle-btn"
                    onClick={() => { navigate(getDefaultAdminRoute()); setActiveSubMenu(null); }}
                    title="Expand sidebar"
                >
                    <i className="bi bi-chevron-right"></i>
                </button>
            )}

            <div className="sidebar-menu flex-grow-1 overflow-auto">
                <div className="menu-section">

                    {hasPermission('dashboard.view') && (
                    <div className="menu-item">
                        <NavLink to="/admin/dashboard" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-grid-3x3-gap-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Dashboard</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('bookings.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/bookings" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon">
                                <i className="bi bi-calendar-check"></i>
                                {isSidebarCollapsed && <MenuIconDot count={bookingCount} />}
                            </span>
                            {!isSidebarCollapsed && <span className="menu-title">Bookings</span>}
                            {!isSidebarCollapsed && <MenuBadge count={bookingCount} />}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('bookings.calendar') && (
                    <div className="menu-item">
                        <NavLink to="/admin/booking-calendar" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-calendar3"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Booking Calendar</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('users.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/users" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-people-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Client Management</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('promo-codes.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/promo-codes" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-ticket-perforated-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Promo Codes</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('transactions.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/transactions" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-credit-card-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Transactions</span>}
                        </NavLink>
                    </div>
                    )}

                    {subMenus.catalogue && (
                    <>
                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'catalogue' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('catalogue')}
                        >
                            <span className="menu-icon"><i className="bi bi-database-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Rooms &amp; Services</span>}
                        </span>
                    </div>
                    {renderMobileSubItems('catalogue')}
                    </>
                    )}

                    {subMenus.memberships && (
                    <>
                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'memberships' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('memberships')}
                        >
                            <span className="menu-icon">
                                <i className="bi bi-award-fill"></i>
                                {isSidebarCollapsed && <MenuIconDot count={membershipCount} />}
                            </span>
                            {!isSidebarCollapsed && <span className="menu-title">Memberships</span>}
                            {!isSidebarCollapsed && <MenuBadge count={membershipCount} />}
                        </span>
                    </div>
                    {renderMobileSubItems('memberships')}
                    </>
                    )}

                    {/* The admin's own notification inbox lives on the header bell,
                        not here. This entry sends notifications out to app users. */}
                    {hasPermission('broadcasts.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/broadcasts" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-send-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Send Notifications</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('pages.index') && (
                    <div className="menu-item">
                        <NavLink to="/admin/pages" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-file-earmark-text-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Pages</span>}
                        </NavLink>
                    </div>
                    )}

                    {subMenus.teamManagement && (
                    <>
                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'teamManagement' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('teamManagement')}
                        >
                            <span className="menu-icon"><i className="bi bi-shield-check"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Team Management</span>}
                        </span>
                    </div>
                    {renderMobileSubItems('teamManagement')}
                    </>
                    )}

                    {hasPermission('settings.view') && (
                    <div className="menu-item">
                        <NavLink to="/admin/settings" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-gear-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Settings</span>}
                        </NavLink>
                    </div>
                    )}

                    {hasPermission('account.view') && (
                    <div className="menu-item">
                        <NavLink to="/admin/account" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-person-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Account</span>}
                        </NavLink>
                    </div>
                    )}

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}
                            onClick={() => setShowLogoutModal(true)}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="menu-icon"><i className="bi bi-box-arrow-right"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Logout</span>}
                        </span>
                    </div>

                </div>

                {!isSidebarCollapsed && (
                    <div className="sidebar-footer">
                        <p className="copyright-text">© Copyright All Right Reserved 2025</p>
                    </div>
                )}
            </div>
        </div>
        <SubSidebar activeSubMenu={activeSubMenu} subMenus={subMenus} counts={subMenuCounts} />

        {showLogoutModal && ReactDOM.createPortal(
            <>
                {/* Full-viewport overlay */}
                <div
                    onClick={() => setShowLogoutModal(false)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.55)',
                        zIndex: 99998,
                        backdropFilter: 'blur(2px)',
                    }}
                />
                {/* Centered dialog */}
                <div
                    style={{
                        position: 'fixed', inset: 0,
                        zIndex: 99999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 16,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                            width: '100%', maxWidth: 400,
                            padding: '2rem',
                            pointerEvents: 'auto',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#fffef0ff', margin: '0 auto 1.25rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="bi bi-box-arrow-right text-base" style={{ fontSize: 28 }}></i>
                        </div>
                        <h5 style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem' }}>
                            Confirm Logout
                        </h5>
                        <p style={{ color: '#6c757d', fontSize: 14, marginBottom: '1.75rem' }}>
                            Are you sure you want to log out of your session?
                        </p>
                        <div className="d-flex gap-3 justify-content-center">
                            <button
                                className="btn btn-light fw-semibold px-4"
                                style={{ borderRadius: 10, minWidth: 110 }}
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn fw-semibold px-4 btn-base"
                                style={{ borderRadius: 10, minWidth: 110 }}
                                onClick={handleLogoutConfirm}
                            >
                                <i className="bi bi-box-arrow-right me-2"></i>Logout
                            </button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        )}
        </>
    );
};

export default Sidebar;
